<template>
  <div
    class="hidden lg:flex fixed z-40 w-44 flex-col pointer-events-none spendzen-side-menu"
    :style="menuStyle"
  >
    <div
      class="flex flex-col overflow-hidden rounded-r-2xl pointer-events-auto shadow-xl border border-gray-100 dark:border-gray-800 transition-all duration-300"
      @mouseenter="hover = true"
      @mouseleave="hover = false"
    >
      <!-- Header fixe : toujours visible -->
      <div
        class="h-14 flex items-center justify-between px-4 bg-white/90 dark:bg-[#0f1114]/90 backdrop-blur-md cursor-pointer border-b border-gray-50 dark:border-gray-800"
        @click="toggleMenu"
      >
        <span
          class="text-xs font-black tracking-widest uppercase bg-gradient-to-tr from-[#00A1A3] to-[#007F80] bg-clip-text text-transparent"
        >
          SPENDZen
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-4 h-4 text-gray-400 transition-transform"
          :class="isOpen ? 'rotate-180' : ''"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      <!-- Menu déroulant -->
      <nav
        v-if="isOpen"
        class="flex flex-col gap-1 py-3 px-3 bg-white/90 dark:bg-[#0f1114]/90 backdrop-blur-md transition-all duration-300 max-h-[70vh] overflow-y-auto scrollbar-hide"
        :class="[isScrolled && !hover ? 'opacity-40' : 'opacity-100']"
      >
        <a
          v-for="item in menuItems"
          :key="item.id"
          href="javascript:void(0);"
          @click="scrollToSection(item.id)"
          :class="[
            'text-[13px] px-2 py-1.5 rounded-md font-medium transition-all duration-200 text-left truncate',
            currentSection === item.id
              ? 'bg-[#00A17A]/10 text-[#00A17A]'
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5',
          ]"
        >
          {{ item.label }}
        </a>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const menuItems = computed(() => [
  { id: "features", label: t("spendzen.menu.features") },
  { id: "how-it-works", label: t("spendzen.menu.how_it_works") },
  { id: "use-cases", label: t("spendzen.menu.use_cases") },
  { id: "pricing", label: t("spendzen.menu.pricing") },
  { id: "kanzen", label: t("spendzen.menu.kanzen") },
  { id: "download", label: t("spendzen.menu.download") },
  { id: "reviews", label: t("spendzen.menu.reviews") },
  { id: "faq", label: t("spendzen.menu.faq") },
]);

const isOpen = ref(true);
const isScrolled = ref(false);
const hover = ref(false);
const currentSection = ref(null);
const footerOverlap = ref(0);

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

const menuStyle = computed(() => {
  const baseTop = 120; // top-30 approx
  const finalTop = baseTop - footerOverlap.value;
  return {
    top: `${finalTop}px`,
  };
});

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const headerOffset = id === "reviews" ? 80 : 150;
    const elementPosition = el.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
    currentSection.value = id;
  }
};

const handleScroll = () => {
  isScrolled.value = window.scrollY > 80;

  // Section detection
  const middle = window.innerHeight / 2;
  for (const item of menuItems.value) {
    const section = document.getElementById(item.id);
    if (section) {
      const rect = section.getBoundingClientRect();
      if (rect.top <= middle && rect.bottom >= middle) {
        currentSection.value = item.id;
        break;
      }
    }
  }

  // Footer avoidance logic
  const footer = document.querySelector("footer");
  if (footer) {
    const footerRect = footer.getBoundingClientRect();
    const menuHeight = isOpen.value ? 400 : 60; // Approximate
    const padding = 40;

    // If distance from bottom of viewport to footer is less than menu height + padding
    const distanceToFooter = footerRect.top;
    const threshold = 120 + menuHeight + padding; // 120 is baseTop

    if (distanceToFooter < threshold) {
      footerOverlap.value = threshold - distanceToFooter;
    } else {
      footerOverlap.value = 0;
    }
  }
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
  setTimeout(handleScroll, 100);
});
onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.spendzen-side-menu {
  left: 1rem; /* Fallback for laptop/small desktop */
}

@media (min-width: 1360px) {
  .spendzen-side-menu {
    /* Push it out into the margin for a centered 1200px layout */
    left: calc(50% - 600px - 5rem);
  }
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
